/**
 * The dashboard — the first tab (plan Stage 4; owner, 2026-10-12: "дешборд має бути першою вкладкою по всій
 * інфі", "максимально повно інформацію по всім процесам").
 *
 * Read top to bottom it answers, in the order a person asks: what is the farm doing **now** (phones,
 * machines, distributions, jobs, the profile pass — always the whole farm), **what needs a person** (every
 * alert, with the phones/accounts it is about written out — never behind a hover), **yesterday** per
 * direction, the **period** per direction against the one before, the **phones** with their accounts, and
 * the **posts** by result with how each grew.
 *
 * The service computes everything (`GET /v1/dashboard`); this only lays it out. A number the phones have
 * not read yet is shown as "not read yet", never as 0 — a zero would read as "the post got nothing".
 * Picking a phone (its row) narrows the page to that phone: that is the phone's page.
 */

import {
  AlertTriangle, BatteryLow, Bot, CircleAlert, ExternalLink, Film, Info, Layers, RefreshCw, Server, Share2, Smartphone,
  TrendingDown, TrendingUp, X,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import type {
  DashAlert, DashDirection, DashFeedItem, DashPhone, DashPhoneRow, DashSums, Dashboard as DashData, PhoneState,
} from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { Card, Chips, Empty, Skeletons, Status } from '../../ui/components'
import { PhoneLink } from '../../ui/PhoneLink'
import { DirectionChip, useDirectionName, useDirections } from '../marketing/directions'
import { TrendChart } from '../marketing/TrendChart'

const n = (v?: number | null) => (v == null ? '—' : v.toLocaleString())

function when(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''
}

function dayLabel(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

const STATE_ORDER: PhoneState[] = ['free', 'posting', 'held', 'reading', 'offline', 'trouble']

/** `direction` given — the page is that direction's statistics (its tab on the direction's page): the
 *  direction is fixed, and the whole farm's "now" and alerts stay on the overview. */
export function Dashboard({ direction: fixed, account }: { direction?: string; account?: string } = {}) {
  const t = useT().dash
  const { api, authEpoch } = useApp()
  const [params, setParams] = useSearchParams()
  const days = Number(params.get('days')) || 1
  const since = params.get('since') ?? ''
  const until = params.get('until') ?? ''
  const [choosing, setChoosing] = useState(!!(since || until))
  const direction = fixed ?? params.get('direction') ?? ''
  const serial = params.get('serial') ?? ''
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
  }
  const setPeriod = (next: Record<string, string>) => {
    const p = new URLSearchParams(params)
    for (const k of ['days', 'since', 'until']) p.delete(k)
    for (const [k, v] of Object.entries(next)) if (v) p.set(k, v)
    setParams(p, { replace: true })
  }
  const custom = !!(since || until)
  const dash = usePoll(() => api.dashboard({ days: custom ? undefined : days, since, until, direction, serial,
                                             account_id: account }),
                       days > 30 || custom ? 60000 : 20000, [api, authEpoch, days, since, until, direction, serial, account])
  const narrow = !!fixed || !!account
  //: the phone picker lists every phone (of the direction, when one is chosen) — not only those the
  //: narrowed page shows, or picking one would leave nothing else to pick
  const allPhones = usePoll(() => api.devices(), 60000, [api, authEpoch])
  const pickable = (allPhones.data ?? []).filter((p) => !direction || (direction === 'none' ? !p.direction_id : p.direction_id === direction))
    .sort((a, b) => (a.name || a.model || a.serial).localeCompare(b.name || b.model || b.serial, undefined, { numeric: true }))
  const dirs = useDirections()
  const nameOf = useDirectionName()
  const d = dash.data
  const phone = serial ? d?.phones.find((p) => p.serial === serial) : undefined

  return (
    <div className="dash col">
      <Card
        title={account ? t.accountStatsTitle : phone ? t.phonePage(phone.name || phone.display_name || phone.serial) : fixed ? t.statsTitle : t.title}
        sub={d ? t.updated(when(d.generated_at)) : t.sub}
        actions={
          <button className="ghost icon" onClick={dash.refresh} title={t.updated('')} aria-label="refresh">
            <RefreshCw className="ico" size={15} aria-hidden />
          </button>
        }
      >
        <div className="dash-filters">
          <Chips value={custom || choosing ? 'custom' : String(days)}
                 onChange={(v) => {
                   if (v === 'custom') { setChoosing(true); return }
                   setChoosing(false)
                   setPeriod({ days: v === '1' ? '' : v })
                 }}
                 options={[...[1, 7, 30, 90, 365].map((p) => ({ id: String(p), label: t.periods[p] ?? t.daysN(p) })),
                           { id: 'custom', label: t.customDates }]} />
          {(choosing || custom) && (
            <span className="dash-dates">
              <input type="date" value={since || d?.period.since || ''} max={until || undefined} aria-label={t.from}
                     onChange={(e) => setPeriod({ since: e.target.value, until: until || d?.period.until || e.target.value })} />
              <span className="tiny dim">—</span>
              <input type="date" value={until || d?.period.until || ''} min={since || undefined} aria-label={t.to}
                     onChange={(e) => setPeriod({ since: since || d?.period.since || e.target.value, until: e.target.value })} />
            </span>
          )}
          {!narrow && (
            <select value={direction} onChange={(e) => set('direction', e.target.value)} aria-label={t.allDirections}>
              <option value="">{t.allDirections}</option>
              {dirs.list.map((x) => <option key={x.direction_id} value={x.direction_id}>{nameOf(x)}</option>)}
              <option value="none">{t.noDirection}</option>
            </select>
          )}
          {!account && <select value={serial} onChange={(e) => set('serial', e.target.value)} aria-label={t.allPhones}>
            <option value="">{t.allPhones}</option>
            {pickable.map((p) => (
              <option key={p.serial} value={p.serial}>{p.name && p.model ? `${p.name} — ${p.model}` : p.name || p.model || p.serial}</option>
            ))}
          </select>}
          {serial && (
            <>
              <button className="quiet" onClick={() => set('serial', '')}><X className="ico" size={14} aria-hidden /> {t.backToAll}</button>
              {phone && <Link className="btn-sm" to={`/phones?device=${encodeURIComponent(phone.device_id)}`}>
                <Smartphone className="ico" size={14} aria-hidden /> {t.openPhone}</Link>}
            </>
          )}
        </div>
        <Status text={dash.error ? dash.error.message : null} bad />
      </Card>

      {!d && !dash.error && <Skeletons count={4} />}
      {d && (
        <>
          {/* The numbers day by day come first — per direction, then per account (owner, 2026-10-12:
              "статистика по акаунтах і органічному трафіку має йти першою, саме графіки"); what broke
              follows, then what the farm is doing this minute. */}
          {!d.readings.any && <div className="dash-note"><Info className="ico" size={15} aria-hidden /> {t.noReadings}</div>}
          {!account && <KpiStrip d={d} />}
          {!account && <TrendsBlock d={d} />}
          {!account && <AccountsTable d={d} />}
          <AccountTrends d={d} big={!!account} />
          {!narrow && <AlertsBlock alerts={d.alerts} onPhone={(s) => set('serial', s)} />}
          <DirectionsBlock d={d} />
          {!narrow && <NowBlock d={d} />}
          {!account && <PhonesBlock rows={d.phones} onPhone={(s) => set('serial', s)} />}
          <FeedBlock d={d} />
        </>
      )}
    </div>
  )
}

// --- now -----------------------------------------------------------------------------------------------

function NowBlock({ d }: { d: DashData }) {
  const t = useT().dash
  const { phones, machines, distributions, jobs, profile_pass: pp } = d.now
  const fin = distributions.finished_24h
  return (
    <Card title={t.nowTitle} sub={t.nowSub}>
      <div className="now-tiles">
        <div className="now-tile wide">
          <span className="now-head"><Smartphone className="ico" size={15} aria-hidden /> {t.phonesTile}
            <strong className="now-big">{phones.total}</strong></span>
          <div className="state-bar" role="img" aria-label={STATE_ORDER.map((s) => `${t.phoneStates[s]} ${phones[s]}`).join(', ')}>
            {STATE_ORDER.map((s) => phones[s] > 0 && (
              <span key={s} className={`seg st-${s}`} style={{ flexGrow: phones[s] }} />
            ))}
          </div>
          <ul className="state-legend">
            {STATE_ORDER.map((s) => (
              <li key={s} className={phones[s] ? '' : 'zero'}><span className={`sw st-${s}`} aria-hidden />
                <strong>{phones[s]}</strong> {t.phoneStates[s]}</li>
            ))}
          </ul>
        </div>
        <div className="now-tile">
          <span className="now-head"><Server className="ico" size={15} aria-hidden /> {t.machinesTile}</span>
          <strong className="now-big">{t.machinesOnline(machines.filter((m) => m.online).length, machines.length)}</strong>
          <ul className="now-list">
            {machines.map((m) => (
              <li key={m.hub_id} className={m.online ? '' : 'bad-text'}>
                <span className={`dot ${m.online ? 'online' : 'offline'}`} aria-hidden /> {m.hub_id}
                <span className="dim"> · {m.phones_online}/{m.phones}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="now-tile">
          <span className="now-head"><Share2 className="ico" size={15} aria-hidden /> {t.distributionsTile}</span>
          <strong className="now-big">{t.distRunning(distributions.running.length)}</strong>
          <span className="tiny dim">{t.distFinished(fin.done ?? 0, fin.partial ?? 0, fin.error ?? 0)}</span>
        </div>
        <div className="now-tile">
          <span className="now-head"><Layers className="ico" size={15} aria-hidden /> {t.jobsTile}</span>
          <strong className="now-big">{t.jobsLine(jobs.running, jobs.queued)}</strong>
          <span className={`tiny ${jobs.failed_24h ? 'warn-text' : 'dim'}`}>{t.jobsDay(jobs.done_24h, jobs.failed_24h)}</span>
          {Object.entries(jobs.failed_by_type).map(([k, v]) => <span key={k} className="tiny warn-text">{k}: {v}</span>)}
        </div>
        <div className="now-tile">
          <span className="now-head"><Bot className="ico" size={15} aria-hidden /> {t.passTile}</span>
          <strong className={`now-big pass-state ${pp.auto ? 'on' : 'off'}`}>{pp.auto ? t.passOn : t.passOff}</strong>
          {pp.running && <span className="tiny">{t.passRunning(pp.running_label || pp.running)}</span>}
          <span className="tiny dim">{t.passDay(pp.reads_24h)}{pp.last_at ? ` · ${when(pp.last_at)}` : ''}</span>
        </div>
      </div>

      <div className="now-two">
        <div>
          <h3 className="dash-h3">{t.heldTitle}</h3>
          {phones.held_list.length === 0 ? <p className="tiny dim">{t.nobodyHolds}</p> : (
            <ul className="held-list">
              {phones.held_list.map((p) => <HeldRow key={p.device_id} p={p} />)}
            </ul>
          )}
        </div>
        <div>
          <h3 className="dash-h3">{t.runningTitle}</h3>
          {distributions.running.length === 0 ? <p className="tiny dim">{t.nothingRunning}</p> : (
            <ul className="held-list">
              {distributions.running.map((r) => (
                <li key={r.distribution_id}>
                  <Link to={`/distributions/${r.distribution_id}`} className="dist-link">
                    <Film className="ico" size={14} aria-hidden /> {when(r.created_at)}
                    {r.requested_by && <span className="dim"> · {r.requested_by}</span>}
                  </Link>
                  <Progress done={r.pushed} total={r.phones} label={t.pushed(r.pushed, r.phones)} />
                  {r.uniquify?.enabled && <Progress done={r.uniquify.done} total={r.uniquify.total}
                                                    label={t.rendered(r.uniquify.done, r.uniquify.total)} />}
                  {r.publish?.enabled && <Progress done={r.publish.done} total={r.publish.total} bad={r.publish.failed}
                                                   label={t.published(r.publish.done, r.publish.total, r.publish.failed)} />}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  )
}

function HeldRow({ p }: { p: DashPhone }) {
  const t = useT().dash
  const who = p.state === 'posting' ? `${t.heldPosting}${p.held_by ? ` · ${p.held_by}` : ''}`
    : p.state === 'reading' ? t.heldReading : p.held_by ?? '—'
  return (
    <li className="held-row">
      <span className={`sw st-${p.state}`} aria-hidden />
      <span className="held-name">{p.name || p.display_name || p.serial}</span>
      <span className="held-who">{who}</span>
      {p.held_for_s != null && <span className="tiny dim">{t.heldFor(Math.max(1, Math.round(p.held_for_s / 60)))}</span>}
    </li>
  )
}

function Progress({ done, total, bad = 0, label }: { done: number; total: number; bad?: number; label: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0
  const badPct = total ? Math.round((bad / total) * 100) : 0
  return (
    <div className="mini-progress">
      <span className="tiny">{label}</span>
      <span className="bar" aria-hidden><span className="fill" style={{ width: `${pct}%` }} />
        {badPct > 0 && <span className="fill bad" style={{ width: `${badPct}%` }} />}</span>
    </div>
  )
}

// --- alerts --------------------------------------------------------------------------------------------

const ALERT_SHOWN = 6

function AlertsBlock({ alerts, onPhone }: { alerts: DashAlert[]; onPhone: (serial: string) => void }) {
  const t = useT().dash
  const [open, setOpen] = useState<Record<string, boolean>>({})
  return (
    <Card title={t.alertsTitle} sub={alerts.length ? String(alerts.reduce((s, a) => s + (a.kind === 'pass_off' ? 0 : a.count), 0)) : undefined}>
      {alerts.length === 0 ? <p className="tiny dim">{t.alertsNone}</p> : (
        <ul className="alert-list">
          {alerts.map((a) => {
            const Icon = a.level === 'bad' ? CircleAlert : a.level === 'warn' ? AlertTriangle : Info
            const items = open[a.kind] ? a.items : a.items.slice(0, ALERT_SHOWN)
            const rest = a.count - items.length
            return (
              <li key={a.kind} className={`alert-row lv-${a.level}`}>
                <Icon className="ico" size={16} aria-hidden />
                <div className="alert-body">
                  <strong>{(t.alert[a.kind] ?? ((c: number) => `${a.kind}: ${c}`))(a.count)}</strong>
                  {items.length > 0 && (
                    <span className="alert-items">
                      {items.map((it, i) => <AlertItem key={i} item={it} onPhone={onPhone} />)}
                      {rest > 0 && (
                        <button className="quiet tiny" onClick={() => setOpen({ ...open, [a.kind]: true })}>{t.more(rest)}</button>
                      )}
                    </span>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function AlertItem({ item, onPhone }: { item: Record<string, any>; onPhone: (serial: string) => void }) {
  const all = useT()
  const t = all.dash
  const label: string = item.label || item.name || item.display_name || item.device_name || item.url || item.hub_id
    || item.caption || item.type || item.job_id || item.publication_id || item.distribution_id || item.serial || '—'
  const extra: string[] = []
  if (item.battery != null && item.state) extra.push(t.battery(item.battery))
  if (item.offline_since) extra.push(when(item.offline_since))
  if (item.doubtful) extra.push(String(item.doubtful))
  if (item.not_found) extra.push(String(item.not_found))
  if (item.phones && item.url) extra.push(all.dash.phonesCount(item.phones))
  if (item.publications && item.url) extra.push(`×${item.publications}`)
  if (item.error) extra.push(item.error)
  if (item.outcome) extra.push(all.profile.outcome[item.outcome] ?? item.outcome)
  if (item.published_at && !item.offline_since) extra.push(when(item.published_at))
  if (item.account_label && item.device_name) extra.push(item.account_label)
  if (item.stats?.views != null) extra.push(`${Number(item.stats.views).toLocaleString()} 👁`)
  if (Array.isArray(item.sites)) extra.push(item.sites.join(' + '))     // accounts_two_sites: which two
  const text = (
    <>
      <span className="ai-label">{label.length > 60 ? `${label.slice(0, 57)}…` : label}</span>
      {extra.length > 0 && <span className="dim"> · {extra.join(' · ')}</span>}
    </>
  )
  if (item.distribution_id) return <Link className="alert-item" to={`/distributions/${item.distribution_id}`}>{text}</Link>
  // an account on two sites is fixed on its page, where an admin sets the one it keeps
  if (item.account_id && Array.isArray(item.sites)) {
    return <Link className="alert-item" to={`/accounts/${encodeURIComponent(item.account_id)}`}>{text}</Link>
  }
  if (item.serial && !item.url) {
    return <button type="button" className="alert-item linkish" onClick={() => onPhone(item.serial)}>{text}</button>
  }
  return <span className="alert-item">{text}</span>
}

// --- directions ----------------------------------------------------------------------------------------

/** Each direction day by day: followers, views, likes where they stood each evening, and posts a day. */
function TrendsBlock({ d }: { d: DashData }) {
  const t = useT().dash
  const dirs = useDirections()
  //: the phones without a direction too — their posts count in the numbers above, so they are drawn here
  const shown = d.directions
  if (shown.length === 0) return null
  const at = (day: string) => `${day}T12:00:00`
  return (
    <Card title={t.trendsTitle} sub={t.trendsSub(shown[0]!.series.length)}>
      <div className="trend-groups">
        {shown.map((x) => {
          const dir = x.direction_id ? dirs.byId.get(x.direction_id) : undefined
          const line = (k: 'followers_total' | 'tile_views_total' | 'likes_total') =>
            x.series.map((s) => ({ at: at(s.day), value: s[k] ?? null }))
          return (
            <section key={x.direction_id ?? 'none'} className="trend-group" data-color={dir?.color}>
              <h3>
                {dir ? <Link to={`/directions/${encodeURIComponent(x.direction_id!)}`}><DirectionChip direction={dir} /></Link>
                     : x.direction_id ? x.name : <span className="dir-chip none">{t.unbound}</span>}
                <span className="tiny dim">{t.phonesAccounts(x.phones, x.phones_online, x.accounts)}</span>
              </h3>
              <div className="trend-grid">
                <TrendChart title={t.followers} hint={t.hintFollowers} points={line('followers_total')} />
                <TrendChart title={t.viewsAll} hint={t.hintViews} points={line('tile_views_total')} />
                <TrendChart title={t.likesAll} hint={t.hintLikes} points={line('likes_total')} />
                <TrendChart title={t.postsPerDay} hint={t.hintPosts} kind="bars" points={x.series.map((s) => ({ at: at(s.day), value: s.posts }))} />
              </div>
            </section>
          )
        })}
      </div>
    </Card>
  )
}

/** The period in five numbers, each against the period before (the social-tools pattern: the headline
 *  first, then the lines behind it). Summed over the directions shown. */
function KpiStrip({ d }: { d: DashData }) {
  const t = useT().dash
  const gain = (k: 'followers' | 'views' | 'likes' | 'posts', which: 'period' | 'previous') => {
    const got = d.directions.map((x) => x[which][k]).filter((v): v is number => v != null)
    return got.length ? got.reduce((a, b) => a + b, 0) : null
  }
  //: where the farm stands now: each direction's latest evening, added up
  const now = (k: 'followers_total' | 'tile_views_total' | 'likes_total') => {
    const got = d.directions.map((x) => [...x.series].reverse().find((s) => s[k] != null)?.[k]).filter((v): v is number => v != null)
    return got.length ? got.reduce((a, b) => a + b, 0) : null
  }
  const tiles: { label: string; total: number | null; k: 'followers' | 'views' | 'likes' }[] = [
    { label: t.followers, total: now('followers_total'), k: 'followers' },
    { label: t.viewsAll, total: now('tile_views_total'), k: 'views' },
    { label: t.likes, total: now('likes_total'), k: 'likes' },
  ]
  const signed = (v: number) => `${v > 0 ? '+' : ''}${v.toLocaleString()}`
  const posts = gain('posts', 'period')
  return (
    <div className="kpi-strip" role="list" aria-label={t.kpiTitle}>
      {/* The big number is the period's (owner, 2026-10-13: switching today / 7 / 30 days changed nothing
          here — the big numbers were the totals, which no period moves); the total sits under it. */}
      {tiles.map(({ label, total, k }) => {
        const g = gain(k, 'period')
        return (
          <div key={k} className="kpi" role="listitem">
            <span className="kpi-label">{label}</span>
            <strong className={`kpi-value${g == null ? ' unknown' : ''} ${(g ?? 0) > 0 ? 'ok-text' : ''}`}>
              {g == null ? '—' : signed(g)}</strong>
            {total == null
              ? <span className="tiny dim">{t.notRead}</span>
              : <span className="tiny dim">{t.totalNow(total.toLocaleString())}</span>}
            {g != null && g !== 0 && <Delta now={g} before={gain(k, 'previous')} />}
          </div>
        )
      })}
      <div className="kpi" role="listitem">
        <span className="kpi-label">{t.posts}</span>
        <strong className="kpi-value">{(posts ?? 0).toLocaleString()}</strong>
        <span className="tiny dim">{t.postsInPeriodShort}</span>
        {posts != null && <Delta now={posts} before={gain('posts', 'previous')} />}
      </div>
      <div className="kpi" role="listitem">
        <span className="kpi-label">{t.clicks}</span>
        <strong className="kpi-value unknown">—</strong>
        <span className="tiny dim">{t.clicksNotYet}</span>
      </div>
    </div>
  )
}

type SortKey = 'name' | 'followers' | 'followers_gained' | 'views' | 'views_gained' | 'posts' | 'read'

/** Every account in one table, sorted by any column (a click on its head); a small line of followers in
 *  each row. The name opens the account. */
function AccountsTable({ d }: { d: DashData }) {
  const t = useT().dash
  const dirs = useDirections()
  const [sort, setSort] = useState<{ k: SortKey; up: boolean }>({ k: 'followers_gained', up: false })
  const rows = d.accounts ?? []
  if (rows.length === 0) return null
  const last = (a: (typeof rows)[number], k: 'followers' | 'tile_views') =>
    [...a.series].reverse().find((x) => x[k] != null)?.[k] ?? null
  const value = (a: (typeof rows)[number]): number | string | null => {
    switch (sort.k) {
      case 'name': return (a.email || a.label || '').toLowerCase()
      case 'followers': return last(a, 'followers')
      case 'views': return last(a, 'tile_views')
      case 'followers_gained': return a.followers_gained ?? null
      case 'views_gained': return a.views_gained ?? null
      case 'posts': return a.posts
      case 'read': return a.last_read_at ?? null
    }
  }
  const sorted = [...rows].sort((x, y) => {
    const a = value(x)
    const b = value(y)
    if (a == null && b == null) return 0
    if (a == null) return 1                                  // unknown always last, whichever way
    if (b == null) return -1
    const c = a < b ? -1 : a > b ? 1 : 0
    return sort.up ? c : -c
  })
  const head = (k: SortKey, label: string, num = true) => (
    <th className={num ? 'num' : undefined} aria-sort={sort.k === k ? (sort.up ? 'ascending' : 'descending') : 'none'}>
      <button className="th-sort" onClick={() => setSort({ k, up: sort.k === k ? !sort.up : k === 'name' })}>
        {label}{sort.k === k ? (sort.up ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  )
  const signed = (v: number | null | undefined) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toLocaleString()}`)
  //: read for the first time inside the period: nothing before to count a gain from — said, not a 0
  const firstRead = (a: (typeof rows)[number]) => !!a.first_read_at && a.first_read_at >= d.period.start
  return (
    <Card title={t.accTableTitle(rows.length)} sub={t.accTableSub}>
      <div className="scroll-x">
        <table className="grid dash-table acc-table">
          <thead><tr>
            {head('name', t.colAccount, false)}
            <th>{t.colFollowersLine}</th>
            {head('followers', t.colFollowers)}
            {head('followers_gained', t.colFollowersGained)}
            {head('views', t.colViews)}
            {head('views_gained', t.colViewsGained)}
            {head('posts', t.colPosts)}
            {head('read', t.colRead)}
          </tr></thead>
          <tbody>
            {sorted.map((a) => {
              const dir = a.direction_id ? dirs.byId.get(a.direction_id) : undefined
              return (
                <tr key={a.account_id}>
                  <td>
                    <Link to={`/accounts/${encodeURIComponent(a.account_id)}`} className="acc-pill" data-color={dir?.color}>
                      {a.email || a.label || `@${a.handle}`}
                    </Link>
                    <div className="tiny dim">{a.device_name || a.serial}</div>
                  </td>
                  <td><FollowersLine days={a.series} color={dir?.color} /></td>
                  <td className="num">{last(a, 'followers')?.toLocaleString() ?? '—'}</td>
                  <td className={`num ${(a.followers_gained ?? 0) > 0 ? 'ok-text' : (a.followers_gained ?? 0) < 0 ? 'bad-text' : ''}`}>
                    {firstRead(a) ? <span className="tiny dim">{t.firstReadInPeriod}</span> : signed(a.followers_gained)}</td>
                  <td className="num">{last(a, 'tile_views')?.toLocaleString() ?? '—'}</td>
                  <td className={`num ${(a.views_gained ?? 0) > 0 ? 'ok-text' : ''}`}>
                    {firstRead(a) ? <span className="tiny dim">{t.firstReadInPeriod}</span> : signed(a.views_gained)}</td>
                  <td className="num">{a.posts}</td>
                  <td className="tiny">{a.last_read_at ? when(a.last_read_at) : <span className="warn-text">{t.notRead}</span>}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

/** The accounts table's line, said in words as well (owner, 2026-10-13: "не ясно що це за точки полоски"):
 *  the number it started at and where it is now beside the line, every day's value on hover, and until a
 *  few days were read no line at all — "read since …", which is what a two-day dash really meant. */
function FollowersLine({ days, color }: { days: { day: string; followers?: number | null }[]; color?: string }) {
  const t = useT().dash
  const read = days.filter((x) => x.followers != null) as { day: string; followers: number }[]
  if (read.length === 0) return <span className="tiny dim">{t.notRead}</span>
  const first = read[0]!
  const last = read[read.length - 1]!
  const hover = read.map((x) => `${dayLabel(x.day)}: ${x.followers.toLocaleString()}`).join('\n')
  if (read.length < 3) {
    return <span className="tiny dim" title={hover}>{t.lineSince(dayLabel(first.day), last.followers.toLocaleString())}</span>
  }
  const change = last.followers - first.followers
  return (
    <span className="followers-line" title={hover}>
      <span className="tiny dim">{first.followers.toLocaleString()}</span>
      <MiniLine values={days.map((x) => x.followers ?? null)} color={color} />
      <span className={`tiny ${change > 0 ? 'ok-text' : change < 0 ? 'bad-text' : 'dim'}`}>{last.followers.toLocaleString()}</span>
      <span className="tiny dim">{t.lineFrom(dayLabel(first.day))}</span>
    </span>
  )
}

/** A row's small line: the shape only, its last value dotted; gaps where a day was not read. */
function MiniLine({ values, color }: { values: (number | null)[]; color?: string }) {
  const known = values.map((v, i) => ({ v, i })).filter((x): x is { v: number; i: number } => x.v != null)
  if (known.length === 0) return <span className="tiny dim">—</span>
  const w = 90
  const h = 24
  const lo = Math.min(...known.map((x) => x.v))
  const hi = Math.max(...known.map((x) => x.v))
  const x = (i: number) => (values.length < 2 ? w / 2 : (i / (values.length - 1)) * (w - 4) + 2)
  const y = (v: number) => h - 3 - ((v - lo) / (hi - lo || 1)) * (h - 6)
  let d = ''
  let open = false
  values.forEach((v, i) => {
    if (v == null) { open = false; return }
    d += `${open ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)} `
    open = true
  })
  const end = known[known.length - 1]!
  return (
    <svg className="mini-line" width={w} height={h} viewBox={`0 0 ${w} ${h}`} data-color={color} aria-hidden>
      <path d={d} />
      <circle cx={x(end.i)} cy={y(end.v)} r="2.5" />
    </svg>
  )
}

/** Every account day by day — a card each, its lines side by side; the name opens the account. */
function AccountTrends({ d, big = false }: { d: DashData; big?: boolean }) {
  const t = useT().dash
  const dirs = useDirections()
  const rows = d.accounts ?? []
  if (rows.length === 0) return null
  const at = (day: string) => `${day}T12:00:00`
  return (
    <Card title={big ? t.trendsTitle : t.accountsTrendsTitle(rows.length)} sub={big ? undefined : t.accountsTrendsSub}>
      <div className="acc-trends">
        {rows.map((a) => {
          const dir = a.direction_id ? dirs.byId.get(a.direction_id) : null
          const line = (k: 'followers' | 'tile_views' | 'likes') => a.series.map((s) => ({ at: at(s.day), value: s[k] }))
          return (
            <section key={a.account_id} className="acc-trend" data-color={dir?.color}>
              {!big && <div className="acc-trend-head">
                <Link to={`/accounts/${encodeURIComponent(a.account_id)}`} className="acc-name">{a.email || a.label || a.handle}</Link>
                {a.handle && <span className="tiny dim">@{a.handle}</span>}
                <span className="tiny dim">{a.device_name || a.serial}</span>
                {dir && <DirectionChip direction={dir} />}
                <span className="tiny dim">{t.postsInPeriod(a.posts)}</span>
                {!a.read && <span className="tiny warn-text">{t.notRead}</span>}
              </div>}
              {big && !a.read && <span className="tiny warn-text">{t.notRead}</span>}
              {(
                <div className={`trend-grid${big ? '' : ' compact'}`}>
                  <TrendChart title={t.followers} hint={big ? t.hintFollowers : undefined} points={line('followers')} />
                  <TrendChart title={t.viewsAll} hint={big ? t.hintViews : undefined} points={line('tile_views')} />
                  <TrendChart title={t.likesAll} hint={big ? t.hintLikes : undefined} points={line('likes')} />
                  {big && <TrendChart title={t.profileViews} hint={t.hintProfileViews} points={a.series.map((x) => ({ at: at(x.day), value: x.profile_views }))} />}
                  <TrendChart title={t.postsPerDay} hint={big ? t.hintPosts : undefined} kind="bars"
                              points={a.series.map((x) => ({ at: at(x.day), value: x.posts ?? 0 }))} />
                </div>
              )}
            </section>
          )
        })}
      </div>
    </Card>
  )
}

/** Each direction over the **chosen** period, against the stretch before it (owner, 2026-10-13: switching
 *  today / 7 / 30 days left these cards as they were — they always showed yesterday). Yesterday alone is
 *  a pick of dates away. The accounts in trouble are as of now, whatever the period. */
function DirectionsBlock({ d }: { d: DashData }) {
  const t = useT().dash
  const dirs = useDirections()
  const p = d.period
  const since = p.since ?? ''
  const until = p.until ?? since
  const label = p.chosen && since
    ? (since === until ? dayLabel(since) : `${dayLabel(since)} — ${dayLabel(until)}`)
    : (t.periods[p.days] ?? t.daysN(p.days))
  return (
    <>
      <Card title={t.dirPeriodTitle(label)} sub={t.dirPeriodSub}>
        <div className="dir-grid">
          {d.directions.map((x) => (
            <div key={x.direction_id ?? 'none'} className="dir-card" data-color={x.direction_id ? dirs.byId.get(x.direction_id)?.color : undefined}>
              <DirHead x={x} />
              <Sums s={x.period} before={x.previous} />
              <Best s={x.period} />
              <div className="dir-trouble">
                <span className="tiny dim">{t.trouble}</span>
                {x.yesterday.trouble.length === 0 ? <span className="tiny ok-text">{t.noTrouble}</span> : (
                  <ul>{x.yesterday.trouble.map((a) => (
                    <li key={a.account_id} className="tiny bad-text">{a.label} — {t.phoneAlert[`account_${a.why}`] ?? a.why}</li>
                  ))}</ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </>
  )
}

function DirHead({ x }: { x: DashDirection }) {
  const t = useT().dash
  const dirs = useDirections()
  const dir = x.direction_id ? dirs.byId.get(x.direction_id) : null
  return (
    <div className="dir-head">
      {dir ? <DirectionChip direction={dir} /> : <span className="dir-chip none">{t.unbound}</span>}
      <span className="tiny dim">{t.phonesAccounts(x.phones, x.phones_online, x.accounts)}</span>
      {x.followers_total != null && <span className="tiny dim">{t.followersTotal(n(x.followers_total))}</span>}
    </div>
  )
}

function Sums({ s, before }: { s: DashSums; before?: DashSums }) {
  const t = useT().dash
  //: nothing read yet: one line, not a row of dashes
  if (s.views == null && s.likes == null && s.followers == null) {
    return (
      <dl className="sums">
        <div><dt>{t.posts}</dt><dd><span className="big">{s.posts.toLocaleString()}</span></dd></div>
        <div className="sums-none"><dd className="tiny dim">{t.numbersNotYet}</dd></div>
      </dl>
    )
  }
  const rows: [string, number | null, number | null | undefined][] = [
    [t.posts, s.posts, before?.posts],
    [t.views, s.views, before?.views],
    [t.likes, s.likes, before?.likes],
    [t.followers, s.followers, before?.followers],
  ]
  return (
    <dl className="sums">
      {rows.map(([label, v, b]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>
            <span className={`big ${v == null ? 'unknown' : ''}`} title={v == null ? t.notRead : undefined}>
              {v == null ? '—' : (label === t.posts ? v.toLocaleString() : `${v >= 0 ? '+' : ''}${v.toLocaleString()}`)}
            </span>
            {v == null && <span className="tiny dim">{t.notRead}</span>}
            {v != null && s.views_from === 'profile' && (label === t.views || label === t.likes) && (
              <span className="tiny dim">{t.fromProfile}</span>
            )}
            {before && v != null && <Delta now={v} before={b} />}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function Delta({ now, before }: { now: number; before?: number | null }) {
  const t = useT().dash
  if (before == null) return null
  if (before === 0) return now > 0 ? <span className="delta up tiny">{t.newSinceBefore}</span> : <span className="delta tiny dim">{t.sameAsBefore}</span>
  const pct = Math.round(((now - before) / before) * 100)
  if (pct === 0) return <span className="delta tiny dim">{t.sameAsBefore}</span>
  const Icon = pct > 0 ? TrendingUp : TrendingDown
  return (
    <span className={`delta tiny ${pct > 0 ? 'up' : 'down'}`}>
      <Icon className="ico" size={12} aria-hidden /> {t.vsBefore(pct > 0 ? '+' : '−', `${Math.abs(pct)}%`)}
    </span>
  )
}

function Best({ s }: { s: DashSums }) {
  const t = useT().dash
  return (
    <div className="best">
      <span className="tiny dim">{t.best}</span>
      {!s.best ? <span className="tiny dim">{t.noBest}</span> : (
        <span className="best-line">
          <span className="best-caption">{s.best.caption || s.best.video_name || '—'}</span>
          <span className="tiny"><strong>+{s.best.views_gained.toLocaleString()}</strong> {t.views} · {s.best.account_label ?? s.best.device_name ?? s.best.serial}</span>
          {s.best.url && <a className="tiny" href={s.best.url} target="_blank" rel="noreferrer">{t.openPost} <ExternalLink className="ico" size={11} aria-hidden /></a>}
        </span>
      )}
    </div>
  )
}

// --- phones --------------------------------------------------------------------------------------------

function PhonesBlock({ rows, onPhone }: { rows: DashPhoneRow[]; onPhone: (serial: string) => void }) {
  const t = useT().dash
  const dirs = useDirections()
  const states = useT().dash.phoneStates
  return (
    <Card title={t.phonesTitle} sub={t.phonesSub}>
      {rows.length === 0 ? <Empty>{t.noAccounts}</Empty> : (
        <div className="scroll-x">
          <table className="grid dash-table">
            <thead>
              <tr>{(['phone', 'direction', 'state', 'accounts', 'followers', 'posts7', 'views7', 'lastPost', 'alerts'] as const)
                .map((k) => <th key={k} className={['followers', 'posts7', 'views7'].includes(k) ? 'num' : ''}>{t.col[k]}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const followers = r.accounts.reduce((s, a) => s + (a.followers ?? 0), 0)
                const anyRead = r.accounts.some((a) => a.followers != null)
                return (
                  <tr key={r.serial} onClick={() => onPhone(r.serial)} className="clickable">
                    <td>
                      <button type="button" className="linkish phone-cell" onClick={(e) => { e.stopPropagation(); onPhone(r.serial) }}>
                        {r.name || r.display_name || r.serial}
                      </button>
                      <span className="tiny dim mono">{r.serial}{r.hub_id ? ` · ${r.hub_id}` : ''}</span>
                    </td>
                    <td>{r.direction_id ? <DirectionChip direction={dirs.byId.get(r.direction_id)} /> : <span className="tiny dim">—</span>}</td>
                    <td>
                      <span className="state-cell"><span className={`sw st-${r.state}`} aria-hidden /> {states[r.state]}</span>
                      {r.battery != null && <span className={`tiny ${r.battery < 20 && !r.charging ? 'bad-text' : 'dim'}`}>
                        {r.battery < 20 && <BatteryLow className="ico" size={12} aria-hidden />} {t.battery(r.battery)}{r.charging ? ' ⚡' : ''}</span>}
                    </td>
                    <td>
                      {r.accounts.length === 0 ? <span className="tiny dim">{t.noAccounts}</span> : r.accounts.map((a) => (
                        <span key={a.account_id} className={`acc-line tiny ${a.state === 'banned' ? 'bad-text' : a.state === 'limited' ? 'warn-text' : ''}`}>
                          {a.label}{a.state !== 'active' ? ` · ${a.state}` : ''}
                        </span>
                      ))}
                    </td>
                    <td className="num">{anyRead ? followers.toLocaleString() : '—'}
                      {r.followers_7d != null && r.followers_7d !== 0 && <span className={`tiny ${r.followers_7d > 0 ? 'ok-text' : 'bad-text'}`}> {r.followers_7d > 0 ? '+' : ''}{r.followers_7d}</span>}</td>
                    <td className="num">{r.posts_7d}</td>
                    <td className="num">{r.views_7d == null ? '—' : `+${r.views_7d.toLocaleString()}`}</td>
                    <td className="tiny">{r.last_post_at ? when(r.last_post_at) : <span className="dim">{t.never}</span>}</td>
                    <td>{r.alerts.map((a) => <span key={a} className={`alert-chip ${/offline|trouble|battery_low|banned/.test(a) ? 'bad' : 'warn'}`}>{t.phoneAlert[a] ?? a}</span>)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

// --- the posts -----------------------------------------------------------------------------------------

function FeedBlock({ d }: { d: DashData }) {
  const t = useT().dash
  const unread = d.feed.filter((p) => !p.stats).length
  return (
    <Card title={t.feedTitle} sub={t.feedSub(d.feed.length, d.feed_total)}>
      <p className="tiny dim feed-explain">{t.feedExplain}</p>
      {unread > 0 && <p className="tiny warn-text">{t.feedUnread(unread, d.feed.length)}</p>}
      {d.feed.length === 0 ? <p className="tiny dim">{t.feedNone}</p> : (
        <ul className="feed">
          {d.feed.map((p) => <FeedItem key={p.publication_id} p={p} />)}
        </ul>
      )}
    </Card>
  )
}

const STAT_KEYS = ['views', 'likes', 'comments', 'shares', 'saves'] as const

/** One post: its picture, what and when and whose, then its numbers under their names — or, when the
 *  phone has not opened it yet, that in words. "How it grew" only where there is a line to draw. */
function FeedItem({ p }: { p: DashFeedItem }) {
  const t = useT().dash
  const tp = useT().profile
  const { api } = useApp()
  const dirs = useDirections()
  const [open, setOpen] = useState(false)
  const [broken, setBroken] = useState(false)
  const reads = p.line.filter((x) => x.views != null).length
  const color = p.direction_id ? dirs.byId.get(p.direction_id)?.color : undefined
  return (
    <li className="feed-item">
      <div className="feed-thumb">
        {p.thumb_url && !broken
          ? <img src={api.artifactUrl(p.thumb_url)} alt="" loading="lazy" onError={() => setBroken(true)} />
          : <span className="tiny dim feed-nopic"><Film className="ico" size={20} aria-hidden /></span>}
      </div>
      <div className="feed-body">
        <span className="feed-caption">{p.caption || p.video_name || (p.source === 'profile' ? t.foundPost : '—')}</span>
        {p.not_recommended && <span className="flag-bad">{t.notRecommendedLong}</span>}
        <span className="tiny dim">{when(p.published_at)}{p.source === 'profile' && <> · {t.found}</>}</span>
        <span className="feed-who">
          <span className="acc-pill" data-color={color}>{p.account_label ?? t.accountUnknown}</span>
          <PhoneLink deviceId={p.device_id} name={p.device_name} serial={p.serial} />
        </span>
        {p.stats ? (
          <dl className="feed-stats">
            {STAT_KEYS.map((k) => (
              <div key={k}><dt>{tp.statNames[k]}</dt><dd>{n(p.stats?.[k])}</dd></div>
            ))}
          </dl>
        ) : <span className="tiny dim feed-none">{t.postNotRead}</span>}
        {p.stats_at && <span className="tiny dim">{tp.statsAt(when(p.stats_at))}{p.ai_label ? ` · ${tp.aiLabel}` : ''}</span>}
        {p.views_gained != null && p.views_gained > 0 && <span className="tiny ok-text">{t.gainedInPeriod(p.views_gained.toLocaleString())}</span>}
        {p.links.length > 0 && <span className="tiny dim">{t.carries(p.links.map((l) => l.name || l.url).join(', '))}</span>}
        <span className="feed-actions">
          {reads >= 2 && <button className="quiet tiny" aria-expanded={open} onClick={() => setOpen(!open)}>{t.growth}</button>}
          {p.url && <a className="btn-sm tiny" href={p.url} target="_blank" rel="noreferrer">{t.openPost} <ExternalLink className="ico" size={11} aria-hidden /></a>}
        </span>
      </div>
      {open && <Growth id={p.publication_id} />}
    </li>
  )
}

/** The post's readings as a line per number, with a table of the same numbers under it. */
function Growth({ id }: { id: string }) {
  const t = useT().dash
  const names = useT().profile.statNames
  const { api } = useApp()
  const got = usePoll(() => api.publicationHistory(id), 120000, [api, id])
  if (!got.data) return <Skeletons count={1} />
  const pts = got.data.points.filter((p) => STAT_KEYS.some((k) => p[k] != null))
  if (pts.length === 0) return <p className="tiny dim">{t.postNotRead}</p>
  return (
    <div className="growth">
      <div className="trend-grid">
        <TrendChart title={names.views ?? ''} hint={t.hintPostViews} points={pts.map((p) => ({ at: p.at, value: p.views ?? null }))} />
        <TrendChart title={names.likes ?? ''} hint={t.hintPostLikes} points={pts.map((p) => ({ at: p.at, value: p.likes ?? null }))} />
      </div>
      <details>
        <summary className="tiny dim">{t.everyReading(pts.length)}</summary>
        <div className="scroll-x">
          <table className="grid dash-table compact">
            <thead><tr><th />{STAT_KEYS.map((k) => <th key={k} className="num">{names[k]}</th>)}</tr></thead>
            <tbody>
              {pts.map((p) => (
                <tr key={p.at}><td className="tiny">{when(p.at)}</td>
                  {STAT_KEYS.map((k) => <td key={k} className="num">{n(p[k])}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  )
}
